import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { armorMessage, dearmorMessage, generatePassphrase, messageFromFile, openMessage, sealMessage, type MessageContent } from '@sss/crypto';
import CopyButton from '../components/Secret/CopyButton';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  MAX_FILE_BYTES,
  downloadBytes,
  fileFits,
  fitFileName,
  formatBytes,
  isShortPassphrase,
  safeFileName,
  sealedFileName,
  textFits,
  toolProblem,
} from '../lib/tools';
import ui from '../styles/ui.module.css';
import secretStyles from '../components/Secret/Secret.module.css';
import styles from './ToolsPage.module.css';

/**
 * Local encrypt/decrypt (AGENT_BRIEF Phase 1, crypto-spec §4): text or a
 * file sealed under a password, entirely in this tab. Nothing here talks
 * to the server; the e2e suite checks that no request carries any of it.
 */
export default function ToolsPage() {
  const { t } = useTranslation();
  usePageTitle(t('nav.tools'));
  return (
    <main className={ui.page}>
      <header className={styles.intro}>
        <p className={ui.eyebrow}>{t('tools.eyebrow')}</p>
        <h1 className={styles.title}>{t('tools.title')}</h1>
        <p className={styles.lead}>{t('tools.lead')}</p>
      </header>
      <div className={styles.columns}>
        <EncryptPanel />
        <DecryptPanel />
      </div>
      <p className={`${ui.hint} ${styles.footnote}`}>{t('tools.footnote')}</p>
    </main>
  );
}

export type Kind = 'text' | 'file';

function KindSwitch({ kind, onChange, legend }: { kind: Kind; onChange: (kind: Kind) => void; legend: string }) {
  const { t } = useTranslation();
  const name = useId();
  return (
    <fieldset className={styles.kind}>
      <legend className="visually-hidden">{legend}</legend>
      {(['text', 'file'] as const).map((k) => (
        <label key={k} className={styles.kindOption}>
          <input type="radio" name={name} value={k} checked={kind === k} onChange={() => onChange(k)} />
          <span>{t(`tools.kind.${k}`)}</span>
        </label>
      ))}
    </fieldset>
  );
}

function PasswordField({
  value,
  onChange,
  label,
  autoComplete,
  shown,
  onShownChange,
  children,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  autoComplete: string;
  shown: boolean;
  onShownChange: (shown: boolean) => void;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div className={ui.field}>
      <label htmlFor={id}>{label}</label>
      <div className={styles.passwordRow}>
        <input
          id={id}
          type={shown ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          spellCheck={false}
          autoCapitalize="off"
          className={styles.password}
        />
        <button type="button" className={styles.smallButton} onClick={() => onShownChange(!shown)} aria-label={shown ? t('tools.password.hideLabel') : t('tools.password.showLabel')}>
          {shown ? t('tools.password.hide') : t('tools.password.show')}
        </button>
        {children}
      </div>
    </div>
  );
}

interface Sealed {
  bytes: Uint8Array<ArrayBuffer>;
  armored: string | null;
  fileName: string;
}

/** Above this the text form is too long to be worth showing; the file is offered instead. */
const MAX_ARMOR_SHOWN = 256 * 1024;

/** Encrypt under a password. The send wizard (docs/adr/0014) embeds it with the kind fixed. */
export function EncryptPanel({ kind: fixedKind }: { kind?: Kind }) {
  const { t } = useTranslation();
  const [chosenKind, setKind] = useState<Kind>(fixedKind ?? 'text');
  const kind = fixedKind ?? chosenKind;
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [generated, setGenerated] = useState(false);
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [sealed, setSealed] = useState<Sealed | null>(null);

  const changed = () => {
    setSealed(null);
    setProblem(null);
  };

  function randomPassword() {
    setPassword(generatePassphrase());
    setGenerated(true);
    // It has to be read out or copied to the recipient, so show it.
    setShown(true);
    changed();
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (kind === 'text' ? text.length === 0 : !file) return setProblem(t(`tools.errors.${kind === 'text' ? 'noText' : 'noFile'}`));
    if (password.length === 0) return setProblem(t('tools.errors.noPassword'));
    setProblem(null);
    setBusy(true);
    try {
      let content: MessageContent;
      let fileName = 'message.sss';
      if (kind === 'file' && file) {
        const name = fitFileName(file.name);
        if (!fileFits(name, file.size)) throw new RangeError('too large');
        content = { kind: 'file', name, data: new Uint8Array(await file.arrayBuffer()) };
        fileName = sealedFileName(name);
      } else {
        if (!textFits(text)) throw new RangeError('too large');
        content = { kind: 'text', text };
      }
      const bytes = await sealMessage(content, password);
      setSealed({ bytes, armored: bytes.length <= MAX_ARMOR_SHOWN ? armorMessage(bytes) : null, fileName });
    } catch (err) {
      setProblem(t(`tools.errors.${err instanceof RangeError ? 'tooLarge' : toolProblem(err)}`, { max: formatBytes(MAX_FILE_BYTES) }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={`${ui.panel} ${styles.panel}`} aria-labelledby="encrypt-title">
      <form className={styles.form} onSubmit={onSubmit} noValidate aria-busy={busy}>
        <div className={secretStyles.header}>
          <h2 id="encrypt-title" className={secretStyles.heading}>
            {t('tools.encrypt.title')}
          </h2>
          <span className={ui.badge}>{t('tools.localBadge')}</span>
        </div>

        {!fixedKind && (
          <KindSwitch
            kind={kind}
            legend={t('tools.encrypt.kindLegend')}
            onChange={(k) => {
              setKind(k);
              changed();
            }}
          />
        )}

        {kind === 'text' ? (
          <label className={ui.field}>
            {t('tools.encrypt.textLabel')}
            <textarea
              rows={6}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                changed();
              }}
              placeholder={t('tools.encrypt.textPlaceholder')}
              autoComplete="off"
              spellCheck={false}
              className={styles.mono}
            />
          </label>
        ) : (
          <label className={ui.field}>
            {t('tools.encrypt.fileLabel')}
            <input
              type="file"
              className={styles.file}
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                changed();
              }}
            />
            <span className={ui.hint}>
              {file ? t('tools.encrypt.fileChosen', { name: file.name, size: formatBytes(file.size) }) : t('tools.encrypt.fileHint', { max: formatBytes(MAX_FILE_BYTES) })}
            </span>
          </label>
        )}

        <PasswordField
          label={t('tools.password.label')}
          value={password}
          autoComplete="new-password"
          shown={shown}
          onShownChange={setShown}
          onChange={(v) => {
            setPassword(v);
            setGenerated(false);
            changed();
          }}
        >
          <button type="button" className={styles.smallButton} onClick={randomPassword}>
            {t('tools.password.random')}
          </button>
        </PasswordField>
        <p className={`${ui.hint} ${styles.passwordHint}`} aria-live="polite">
          {generated
            ? t('tools.password.generated')
            : isShortPassphrase(password)
              ? t('tools.password.short')
              : t('tools.password.hint')}
        </p>
        {generated && <CopyButton value={password} label={t('tools.password.copy')} className={styles.copyPassword} />}

        {problem && (
          <p role="alert" className={ui.error}>
            {problem}
          </p>
        )}

        <button type="submit" className={ui.button} disabled={busy}>
          {busy ? t('tools.encrypt.busy') : t('tools.encrypt.submit')}
        </button>
      </form>

      {/* A short announcement, not the result itself: nobody wants base64 read aloud. */}
      <p role="status" className="visually-hidden">
        {sealed ? t('tools.encrypt.done') : ''}
      </p>
      {sealed && (
        <div className={styles.result}>
          <h3 className={styles.resultTitle}>{t('tools.encrypt.resultTitle')}</h3>
          {sealed.armored !== null && (
            <textarea readOnly rows={7} wrap="off" value={sealed.armored} aria-label={t('tools.encrypt.armoredLabel')} className={`${styles.mono} ${styles.armored}`} data-testid="sealed-message" />
          )}
          <div className={secretStyles.actions}>
            {sealed.armored !== null && <CopyButton value={sealed.armored} label={t('tools.encrypt.copy')} className={ui.button} />}
            <button type="button" className={sealed.armored !== null ? ui.buttonSecondary : ui.button} onClick={() => downloadBytes(sealed.bytes, sealed.fileName)}>
              {t('tools.encrypt.download', { name: sealed.fileName })}
            </button>
          </div>
          <p className={ui.hint}>{t('tools.encrypt.resultHint')}</p>
        </div>
      )}
    </section>
  );
}

type Opened = { kind: 'text'; text: string } | { kind: 'file'; name: string; data: Uint8Array<ArrayBuffer> };

function DecryptPanel() {
  const { t } = useTranslation();
  const [kind, setKind] = useState<Kind>('text');
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [opened, setOpened] = useState<Opened | null>(null);
  const [shown, setShown] = useState(false);

  const changed = () => {
    setOpened(null);
    setProblem(null);
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (kind === 'text' ? text.trim().length === 0 : !file) return setProblem(t(`tools.errors.${kind === 'text' ? 'noMessage' : 'noFile'}`));
    if (password.length === 0) return setProblem(t('tools.errors.noPassword'));
    setProblem(null);
    setBusy(true);
    try {
      const bytes = kind === 'file' && file ? messageFromFile(new Uint8Array(await file.arrayBuffer())) : dearmorMessage(text);
      const content = await openMessage(bytes, password);
      setOpened(content.kind === 'text' ? content : { kind: 'file', name: safeFileName(content.name), data: new Uint8Array(content.data) });
    } catch (err) {
      setProblem(t(`tools.errors.${toolProblem(err)}`, { max: formatBytes(MAX_FILE_BYTES) }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={`${ui.panel} ${styles.panel}`} aria-labelledby="decrypt-title">
      <form className={styles.form} onSubmit={onSubmit} noValidate aria-busy={busy}>
        <div className={secretStyles.header}>
          <h2 id="decrypt-title" className={secretStyles.heading}>
            {t('tools.decrypt.title')}
          </h2>
        </div>

        <KindSwitch
          kind={kind}
          legend={t('tools.decrypt.kindLegend')}
          onChange={(k) => {
            setKind(k);
            changed();
          }}
        />

        {kind === 'text' ? (
          <label className={ui.field}>
            {t('tools.decrypt.textLabel')}
            <textarea
              rows={6}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                changed();
              }}
              placeholder={t('tools.decrypt.textPlaceholder')}
              autoComplete="off"
              spellCheck={false}
              className={styles.mono}
            />
          </label>
        ) : (
          <label className={ui.field}>
            {t('tools.decrypt.fileLabel')}
            <input
              type="file"
              accept=".sss,.txt,application/octet-stream,text/plain"
              className={styles.file}
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                changed();
              }}
            />
          </label>
        )}

        <PasswordField
          label={t('tools.password.label')}
          value={password}
          autoComplete="off"
          shown={shown}
          onShownChange={setShown}
          onChange={(v) => {
            setPassword(v);
            changed();
          }}
        />

        {problem && (
          <p role="alert" className={ui.error}>
            {problem}
          </p>
        )}

        <button type="submit" className={ui.button} disabled={busy}>
          {busy ? t('tools.decrypt.busy') : t('tools.decrypt.submit')}
        </button>
      </form>

      <p role="status" className="visually-hidden">
        {opened ? t('tools.decrypt.done') : ''}
      </p>
      {opened && (
        <div className={styles.result}>
          <h3 className={styles.resultTitle}>{t('tools.decrypt.resultTitle')}</h3>
          {opened.kind === 'text' ? (
            <>
              {/* Scrolls when long, so it must take keyboard focus. */}
              <pre className={secretStyles.secretText} data-testid="decrypted-text" tabIndex={0} aria-label={t('tools.decrypt.resultTitle')}>
                {opened.text}
              </pre>
              <div className={secretStyles.actions}>
                <CopyButton value={opened.text} className={ui.button} />
              </div>
            </>
          ) : (
            <>
              <p className={styles.fileFacts}>
                <span className={ui.mono} data-testid="decrypted-file-name">
                  {opened.name}
                </span>{' '}
                <span className={ui.hint}>{formatBytes(opened.data.length)}</span>
              </p>
              <div className={secretStyles.actions}>
                <button type="button" className={ui.button} onClick={() => downloadBytes(opened.data, opened.name)}>
                  {t('tools.decrypt.download')}
                </button>
              </div>
              <p className={ui.hint}>{t('tools.decrypt.fileWarning')}</p>
            </>
          )}
        </div>
      )}
    </section>
  );
}
